import React, { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Button } from '@lightspeed/flame/Button';
import { Flex } from '@lightspeed/flame/Core';
import { Text } from '@lightspeed/flame/Text';

import { InputBaseTile, InputBaseTileProps } from '~/renderer/components/UI/InputBaseTile';
import { CFD_MESSAGES } from '~/renderer/hooks/socket/types';
import { useSetupAblyCustomer } from '~/renderer/hooks/socket/use-setup-ably-customer';
import { useSocket } from '~/renderer/hooks/socket/use-setup-socket';
import {
  BoxWithTopMargin,
  FullBoxSmallScreen,
  FullHeightFlex,
  StyledFlexCustomerInputs,
  StyledFlexWrapper,
  StyledHeader,
  StyledPhoneInput,
  TitleWrapper,
  WhiteLargeText,
} from '~/renderer/modules/RegisterCustomer/RegisterCustomer.style';
import { ROUTE_REGISTER_CUSTOMER } from '~/renderer/routes';
import { getCfdOptions, selectCountryCode } from '~/renderer/store/cfd/selectors';
import { connectionActions } from '~/renderer/store/connection';
import { getAblyCustomerConnectionStatus } from '~/renderer/store/connection/selectors';
import { getHubBackendEnvironmentUrl } from '~/renderer/store/hub/selectors';
import { ANALYTICS_EVENTS } from '~/renderer/store/middleware/types';
import { receiptActions } from '~/renderer/store/receipt';
import {
  getCustomerInfo,
  getMobilePhone,
  getPrimaryEmail,
  getSaleId,
} from '~/renderer/store/receipt/selectors';
import {
  channelNameCustomer as channelNameCustomerFromUrl,
  generateRandomString,
  isBrowserCFD,
  isCustomerCFD,
} from '~/renderer/utils';
import { track } from '~/renderer/utils/analytics';

import QRCodeImage from './QRCodeImage';
import ScreenCancel from './Screens/Cancel';
import ScreenConnecting from './Screens/Connecting';
import ScreenDevice from './Screens/Device';
import ScreenSave from './Screens/Save';
import ScreenUnavailable from './Screens/Unavailable';

const emailRegex = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/;

export const CustomerCFDStatus = {
  CANCEL: 'canceled',
  SAVE: 'saved',
};

const InputElement = ({ label, ...rest }: { label: string } & InputBaseTileProps) => {
  return (
    <Flex flexDirection="column">
      {label ? <WhiteLargeText size="large">{label}</WhiteLargeText> : null}
      <InputBaseTile {...rest} />
    </Flex>
  );
};

const RegisterCustomer: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const {
    firstName: savedFirstName = '',
    lastName: savedLastName = '',
    customerId,
  } = useSelector(getCustomerInfo) || {};
  const { shopName } = useSelector(getCfdOptions);
  const savedPrimaryEmail = useSelector(getPrimaryEmail) ?? '';
  const savedMobilePhone = useSelector(getMobilePhone) ?? '';
  const saleId = useSelector(getSaleId);
  const countryCode = useSelector(selectCountryCode);
  const { channelNameCustomer } = useSelector(state => state.connection);
  const ablyCustomerConnectionStatus = useSelector(getAblyCustomerConnectionStatus);
  const hubBackendEnvironmentUrl = useSelector(getHubBackendEnvironmentUrl);
  const baseAuthUrl = hubBackendEnvironmentUrl || process.env.HUB_BACKEND_URL || '';

  const origin = isBrowserCFD
    ? window.location.origin
    : baseAuthUrl.replace('hub-backend', 'retail-cfd');

  const dispatch = useDispatch();
  const publish = useSocket();
  const publishCustomer = useSetupAblyCustomer();
  const coercePublish = isCustomerCFD ? publishCustomer : publish;

  const [isSaved, setIsSaved] = React.useState(false);
  const [firstName, setFirstName] = React.useState(savedFirstName);
  const [lastName, setLastName] = React.useState(savedLastName);
  const [phone, setPhone] = React.useState<string>(savedMobilePhone);
  const [email, setEmail] = React.useState(savedPrimaryEmail);

  React.useEffect(() => {
    dispatch(
      connectionActions.updateConnectionChannelNameCustomer(
        channelNameCustomerFromUrl || generateRandomString(23),
      ),
    );

    return () => {
      dispatch(connectionActions.updateConnectionChannelNameCustomer(''));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    setFirstName(savedFirstName);
    setLastName(savedLastName);
    setEmail(savedPrimaryEmail);
  }, [savedFirstName, savedLastName, savedPrimaryEmail]);

  React.useEffect(() => {
    // @NOTE Because the component doesn't update its internal state
    const phoneInput = document.getElementById('phone') as HTMLInputElement;
    if (phoneInput) {
      Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set?.call(
        phoneInput,
        savedMobilePhone,
      );
      // This will trigger a new render wor the component
      phoneInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }, [savedMobilePhone]);

  let countryPriority;
  // Because CA and US share the same +1 code, if the shop's country is CA it is prioritized
  if (countryCode?.toLowerCase() === 'ca') {
    countryPriority = { ca: 0, us: 2 };
  }
  // Autocomplete input suggestion should _never_ appear on the shop's CFD
  const autocompleteProp = isCustomerCFD ? 'on' : 'none';
  const isValidEmail = email === '' || emailRegex.test(email);
  const isEmptyInputs = !firstName && !lastName && !phone && !email;

  const handleCancelClick = () => {
    coercePublish?.(CFD_MESSAGES.CUSTOMER_CANCEL);
    if (isCustomerCFD) {
      window.history.replaceState(
        {},
        document.title,
        `/?channelNameCustomer=&status=${CustomerCFDStatus.CANCEL}#${ROUTE_REGISTER_CUSTOMER}`,
      );
    } else {
      dispatch(
        receiptActions.setCustomerInfoVisibility({
          isCustomerInfoViewVisible: false,
          isSaleVisible: true,
        }),
      );
      history.push('/');
    }
  };

  const handleSaveClick = async () => {
    coercePublish?.(CFD_MESSAGES.CUSTOMER_SAVE, {
      customerId,
      saleId,
      firstName,
      lastName,
      phones: { mobile: phone },
      emails: { primary: email },
    });

    await track(ANALYTICS_EVENTS.REGISTER_CUSTOMER_CFD, {
      isMobile: isCustomerCFD,
      isDesktop: isBrowserCFD,
      isNewCustomer: !customerId,
    });

    if (isCustomerCFD) {
      window.history.replaceState(
        {},
        document.title,
        `/?channelNameCustomer=&status=${CustomerCFDStatus.SAVE}#${ROUTE_REGISTER_CUSTOMER}`,
      );
    } else {
      setIsSaved(true);

      // Display the confirmation screen for 1.5s before going back to the sale
      setTimeout(() => {
        dispatch(
          receiptActions.setCustomerInfoVisibility({
            isCustomerInfoViewVisible: false,
            isSaleVisible: true,
          }),
        );
      }, 2000);
    }
  };

  return (
    <FullHeightFlex flexDirection="column">
      <TitleWrapper p="4">
        <StyledHeader data-testid="customer-info-title" fontWeight="bold">
          {t('hub.cfd.registerCustomer.title')}
        </StyledHeader>
      </TitleWrapper>
      {isCustomerCFD && shopName ? (
        <TitleWrapper p="2">
          <Text color="white">{shopName}</Text>
        </TitleWrapper>
      ) : null}
      <StyledFlexWrapper alignItems="center" justifyContent="center" flex="1">
        <ScreenSave isSaved={isSaved} />
        <ScreenCancel />
        <ScreenConnecting />
        <ScreenDevice />
        <ScreenUnavailable />

        {(!isCustomerCFD && ablyCustomerConnectionStatus !== 'connected' && !isSaved) ||
        (isCustomerCFD && ablyCustomerConnectionStatus === 'connected') ? (
          <>
            <StyledFlexCustomerInputs
              flexDirection="column"
              width="100%"
              isCustomerCFD={isCustomerCFD}
            >
              <Flex
                flexDirection="row"
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
              >
                <FullBoxSmallScreen>
                  <InputElement
                    label={t('hub.cfd.registerCustomer.firstName')}
                    autoComplete={autocompleteProp}
                    aria-label="customer-first-name"
                    type="text"
                    spellCheck="false"
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value)}
                    value={firstName}
                    style={{ width: '100%' }}
                  />
                </FullBoxSmallScreen>
                <FullBoxSmallScreen ml={2}>
                  <InputElement
                    label={t('hub.cfd.registerCustomer.lastName')}
                    autoComplete={autocompleteProp}
                    aria-label="customer-last-name"
                    type="text"
                    spellCheck="false"
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setLastName(e.target.value)}
                    value={lastName}
                    style={{ width: '100%' }}
                  />
                </FullBoxSmallScreen>
              </Flex>
              <BoxWithTopMargin>
                <Flex flexDirection="column">
                  <WhiteLargeText size="large">
                    {t('hub.cfd.registerCustomer.phone')}
                  </WhiteLargeText>
                  <StyledPhoneInput
                    inputProps={{
                      id: 'phone',
                    }}
                    autoComplete={autocompleteProp}
                    inputClass="phone-input-ele"
                    buttonClass="phone-country-btn"
                    dropdownClass="phone-dropdown"
                    alwaysDefaultMask={false}
                    disableSearchIcon
                    country={countryCode?.toLowerCase()}
                    priority={countryPriority}
                    value={phone ?? ''}
                    onChange={(phoneNumber: string) => setPhone(phoneNumber)}
                    enableSearch
                  />
                </Flex>
              </BoxWithTopMargin>
              <BoxWithTopMargin>
                <InputElement
                  label={t('hub.cfd.registerCustomer.email')}
                  autoComplete={autocompleteProp}
                  aria-label="custom-email"
                  type="email"
                  className={!isValidEmail ? 'input-error' : ''}
                  spellCheck="false"
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                  value={email}
                />
              </BoxWithTopMargin>

              <Flex flexDirection="row" justifyContent="space-evenly" mt={4}>
                <Button
                  variant="default"
                  aria-label="cancel-customer-info-change"
                  width={140}
                  onClick={handleCancelClick}
                  size="xlarge"
                  fill
                  borderRadius={4}
                >
                  <Text size="xlarge">{t('hub.cancel')}</Text>
                </Button>

                <Button
                  borderRadius={4}
                  fill
                  variant="secondary"
                  size="xlarge"
                  width={140}
                  aria-label="save-customer-info"
                  onClick={handleSaveClick}
                  disabled={isEmptyInputs || !isValidEmail}
                >
                  <Text size="xlarge">{t('hub.save')}</Text>
                </Button>
              </Flex>
            </StyledFlexCustomerInputs>
            {!isCustomerCFD && origin && channelNameCustomer ? (
              <Flex alignItems="center" flexDirection="column" p={4}>
                <Text mb={4} color="white" style={{ maxWidth: '240px', textAlign: 'center' }}>
                  {t('hub.cfd.registerCustomer.scanQRCode')}
                </Text>
                <QRCodeImage
                  url={`${origin.replace(/\/$/, '')}/?channelNameCustomer=${channelNameCustomer}${
                    window.location.hash
                  }`}
                />
              </Flex>
            ) : null}
          </>
        ) : null}
      </StyledFlexWrapper>
    </FullHeightFlex>
  );
};

export { RegisterCustomer };
